import { useEffect, useRef, useState, type ReactNode } from "react";
import { CloseIcon, TimerIcon } from "../components/Icons";
import { recordedBy } from "./attribution";
import { RichText, spokenText } from "./RichText";
import { useReadout, type Readout } from "./readout";
import {
  fetchAuditDocument,
  fetchAuditDocumentArchive,
  fetchPortalDocument,
  FINDING_OUTCOMES,
  type FindingRow,
  type RailRow,
  type SessionState,
  type TranscriptMessage,
} from "../api/client";

/**
 * The three-column session console — M4-1, screens 52 to 69.
 *
 * Agenda rail, transcript, document register. A port of the prototype's console: the grid
 * template, the message shapes and the field scale are its, and every colour is a token —
 * so the four finding badge colours are the ones `design/tokens.json` fixes and a
 * certification body's accent reaches none of them (ADR 0007).
 *
 * **The same component serves all four viewers.** What differs is the footer, and the
 * footer is passed in — because what a viewer may do is decided on the server and arrives
 * as `state.viewer`. Screens 68 and 69 are this console with a sentence where the composer
 * would be, and there is nothing to hide because there is nothing to render.
 */
export function SessionConsole({
  state,
  footer,
  typing,
  onEditFinding,
  onUpload,
  onMark,
  onMarkAll,
  nav,
  status,
  actions,
  pendingMessage,
}: {
  state: SessionState;
  footer: ReactNode;
  /**
   * Complao has the turn and is composing — the thread shows the three dots.
   *
   * Defaults to the server's own register of turns in flight, which is the same boolean
   * behind the auditor's banner (`broadcast.is_generating`, ADR 0022) — so the two screens
   * cannot disagree about whose turn it is. A console passes its own only to cover the
   * moment between a request leaving it and the first frame coming back.
   */
  typing?: boolean;
  onEditFinding?: (finding: FindingRow) => void;
  /**
   * The client's own console only — M14a-1. Absent for every other viewer, because an
   * auditor uploading into the client's register would misattribute the evidence.
   */
  onUpload?: (files: File[], answering?: string) => Promise<void>;
  /** The lead auditor's console only — M15-6. The mark is the audit team's statement. */
  onMark?: (documentId: string, reviewed: boolean) => Promise<void>;
  /**
   * The same mark over the whole register, in one press. The lead auditor's console only,
   * for the same reason `onMark` is: an assessor reads *reviewed* as the audit team's word.
   */
  onMarkAll?: (reviewed: boolean) => Promise<void>;
  /**
   * The banner's top strip — the way *out* of the session, on the left.
   *
   * The audit day screen has no header of its own any more: the client's page carried a
   * title, a reference line, a status pill, an export and a link back to the portal, and
   * every one of them named something the banner already stood for or could hold. So the
   * banner is the header, and these two slots are what a viewer may do from it. They differ
   * per viewer — the client leaves to their portal, the lead auditor has a tray and a
   * restart — which is why they arrive as nodes rather than being decided here.
   */
  nav?: ReactNode;
  /**
   * Whether the audit is live, paused or closed — drawn ahead of its name.
   *
   * It leads the bar rather than trailing it because it changes what everything after it
   * means: the same name, clock and tally read one way on a session in progress and another
   * on one that closed an hour ago. It arrives as a node for the same reason `actions` does
   * — the client's console and the auditor's do not agree on the wording, and the auditor's
   * has a fourth state (*awaiting the client*) the client's cannot be in.
   */
  status?: ReactNode;
  /** The banner's controls, against the right edge. */
  actions?: ReactNode;
  /**
   * Text sent alongside documents that are still syncing — held back from `sendAuditeeMessage`
   * until retrieval can see what it was sent with (migration 0035), but shown here the moment
   * it was typed, so the client reads it beside the document tile rather than watching it
   * appear to vanish until the sync finishes. Never written to the transcript itself; this is
   * a local echo, not a second record of the turn.
   */
  pendingMessage?: string | null;
}) {
  // The server defaults every list; the generated types still mark them optional, and a
  // console that renders `undefined.map` is a console that goes blank mid-audit.
  const rail = state.rail ?? [];
  const transcript = state.transcript ?? [];
  const findings = state.findings ?? [];

  // **Readout — M31.** One hook per console, because `speechSynthesis` is one queue per
  // document: pressing a second message stops the first rather than talking over it, and
  // the id held here is what lets exactly one speaker show as playing. It is pressed and
  // never automatic, and both viewers get it — a readout writes nothing to the record and
  // says nothing about who is conducting the audit, which is what kept M30's mic to the
  // client's console alone.
  const readout = useReadout();

  // One place decides how a document opens for whoever is looking — the auditee reaches
  // their own upload through the portal's own credential, everyone else through the
  // certification body's. Both `Register` and the transcript's document cards call this;
  // a document card also uses `resolveDocumentUrl` on its own to load an image thumbnail.
  const onOpenDocument = (documentId: string, filename: string) =>
    void openDocument(state, documentId, filename);
  const resolveDocumentUrl = (documentId: string) => documentUrl(state, documentId);
  // Which of the transcript's own document tiles are still mid-ingestion — the mini loader
  // beside a just-sent document, distinct from the composer-wide lock `corpus_sync` drives.
  const syncingDocumentIds = new Set(state.documents_syncing ?? []);

  return (
    <div className={state.viewer.observing ? "console observe" : "console"}>
      {/* **One row, in the order the day is read in.** Where it stands, what it is, how far
          through it is, what it has found, and what may be done about that — the way out on
          the left because that is where a reader's eye starts, the controls on the right
          against the edge the register keeps.

          It used to stack in two tiers because nine loose items wrapped at laptop width and
          the first to fall was the tally, which then read as a continuation of the clock it
          landed under. The fix is grouping, not stacking: the clock and its bar are one
          object, the four grades are one object, the controls are icons.

          *Current section* is not here. The agenda rail draws the same fact three inches
          below and marks the section it is on; the banner was restating it. */}
      <div className="ctop">
        {nav ? <div className="cnav-side">{nav}</div> : null}
        {status ? <div className="cstatus">{status}</div> : null}
        <div className="cid">
          <b title={state.client_organisation}>{state.client_organisation}</b>
          {/* The identity line the page header used to carry, in the order a reader needs
              it: what kind of audit, against what, under what reference. It says *Stage 1
              audit* and not *in session* — the status ahead of it is the one thing saying
              whether it is live, paused or closed, and a fixed "in session" beside a pill
              reading *Audit closed* is a header arguing with itself.

              It truncates on a narrow window and goes altogether below 1180px, and it holds
              a `title` for both cases. Of everything on this bar it is the one part a
              reader can recover elsewhere — the plan the session was opened from carries
              the standard and the reference.

              **It is three clauses and stays three.** The follow-up cap was a fourth,
              appended through a `meta` slot. It is a rule the model follows rather than a
              fact about which audit this is, and the place a reader meets it is the third
              follow-up itself — where the Minor NC it forces arrives with `origin = policy`
              and says so. */}
          <span
            title={`Stage 1 audit · ${state.standards.join(" · ")} · ${state.reference}`}
          >
            Stage 1 audit · {state.standards.join(" · ")} ·{" "}
            <span className="ref">{state.reference}</span>
          </span>
        </div>
        <div className="cgap" />
        <AuditClock state={state} />
        <span className="crule" aria-hidden="true" />
        <Tally state={state} />
        {state.viewer.observing ? (
          <span className="pill p-mute">
            {state.viewer.role === "auditee" ? "You are the client" : "Observing only"}
          </span>
        ) : null}
        <div className="cactions">{actions}</div>
      </div>

      <Rail rows={rail} />
      <div className="cmid">
        <Thread
          transcript={transcript}
          findings={findings}
          typing={typing ?? (state.generating === true && !state.floor_held)}
          onEditFinding={onEditFinding}
          onOpenDocument={onOpenDocument}
          resolveDocumentUrl={resolveDocumentUrl}
          readout={readout}
          syncingDocumentIds={syncingDocumentIds}
          pendingMessage={pendingMessage}
          pendingAuthorName={state.viewer.name}
        />
        <div className="cbar">{footer}</div>
      </div>
      <Register
        state={state}
        onUpload={onUpload}
        onMark={onMark}
        onMarkAll={onMarkAll}
        onOpenDocument={onOpenDocument}
      />
    </div>
  );
}

/**
 * The four grades, always all four.
 *
 * A tally that dropped its zeroes would render three badges on one audit and four on
 * another, and a reader would have to already know which grade was missing to see that it
 * was zero rather than absent.
 *
 * **Each segment opens the findings behind its number.** The count answers *how many* and
 * the reader's next question is always *which* — which previously meant scrolling the
 * transcript for four coloured badges scattered through an hour of it. The panel opens
 * beside the chip, holds one grade at a time, and closes on its cross, on Escape, or on a
 * click anywhere else.
 */
function Tally({ state }: { state: SessionState }) {
  // Which grade's panel is open, or none. One at a time: two panels beside a 32px chip
  // would overlap each other and the transcript beneath.
  const [open, setOpen] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const findings = state.findings ?? [];

  // Escape and a click anywhere outside close it. A panel that could only be closed from
  // its own cross would sit over the transcript while the audit ran underneath it.
  useEffect(() => {
    if (!open) return;
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    const down = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(null);
    };
    window.addEventListener("keydown", key);
    window.addEventListener("mousedown", down);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener("mousedown", down);
    };
  }, [open]);

  return (
    <div className="tallywrap" ref={wrap}>
      <span className="tally">
        {FINDING_OUTCOMES.map((outcome) => {
          const total = (state.tally ?? {})[outcome.key] ?? 0;
          // ⚠ **Pending is marked beside the count, never merged into it.** ADR 0024 counts a
          // proposal in the tally because the client can now read it — but *determined* and
          // *proposed* are different facts, and a number that merged them would tell the lead
          // auditor the audit holds a Major NC before they had read it. The dot is the mark;
          // the segment's title and the tray beneath say how many and what they are.
          const pending = (state.pending ?? {})[outcome.key] ?? 0;
          return (
            <button
              type="button"
              key={outcome.key}
              className={open === outcome.key ? "on" : undefined}
              aria-expanded={open === outcome.key}
              title={
                pending
                  ? `${outcome.label} — ${total}, of which ${pending} awaiting your determination`
                  : `${outcome.label} — ${total}`
              }
              onClick={() =>
                setOpen((current) => (current === outcome.key ? null : outcome.key))
              }
            >
              {/* The grade's own colour, on the grade's own letters. The count beside it is
                  ink: four coloured numerals on one chip would read as four severities of
                  number rather than four counts. */}
              <i
                className="tkey"
                style={{ color: `var(--finding-${slug(outcome.key)}-text)` }}
              >
                {outcome.short}
              </i>
              <b className="tnum">{total}</b>
              {pending ? (
                <span
                  className="tdot"
                  style={{ background: `var(--finding-${slug(outcome.key)}-border)` }}
                />
              ) : null}
            </button>
          );
        })}
      </span>
      {open ? (
        <TallyPanel outcome={open} findings={findings} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}

/**
 * The findings behind one segment of the tally — what the number is made of.
 *
 * The count says four Minor NCs; this says which clauses they are against and what each one
 * states. It is a **read**, and only a read: disposing of a proposal is the tray's, amending
 * a finding is the composer's, and a panel that offered either would be a second path to an
 * act the record has one path for.
 *
 * ⚠ **A proposal is labelled, and its statement is still the model's.** ADR 0024 shows the
 * client a pending proposal marked preliminary; the same row read from the tally carries the
 * same mark, because a lead auditor scanning the grades should not have to open the tray to
 * find out which of these four a human has actually determined.
 */
function TallyPanel({
  outcome,
  findings,
  onClose,
}: {
  outcome: string;
  findings: FindingRow[];
  onClose: () => void;
}) {
  const grade = FINDING_OUTCOMES.find((entry) => entry.key === outcome);
  // ⚠ **A rejected proposal is not under its grade, because it is not in the count.** The
  // server drops it from the tally and from the rail (`_tally`, `_rail_checkpoints`): it is
  // the record that a human read what a machine proposed and disagreed, and listing it here
  // would show three rows behind a count of two. The row is retained — the report is where
  // the overruling is read, not the banner.
  const rows = findings.filter(
    (finding) =>
      finding.outcome === outcome &&
      !(finding.origin === "ai_proposed" && finding.disposition === "rejected"),
  );

  return (
    <div className="tpop" role="dialog" aria-label={`${grade?.label ?? outcome} findings`}>
      <div className="tpophead">
        <span className={`badge badge--${slug(outcome)}`}>{grade?.label ?? outcome}</span>
        <span className="tpopn">
          {rows.length} finding{rows.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          className="tpopx"
          onClick={onClose}
          title="Close"
          aria-label="Close"
        >
          <CloseIcon size={14} />
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="tpopnone">
          Nothing graded {(grade?.label ?? outcome).toLowerCase()} yet.
        </p>
      ) : (
        <div className="tpoprows">
          {rows.map((finding) => {
            const preliminary = finding.origin === "ai_proposed" && !finding.disposition;
            return (
              <div className="tpoprow" key={finding.id}>
                <div className="tpopmeta">
                  <span className="cl">Cl. {finding.clause_ref}</span>
                  <span className="tpopsec">{finding.section}</span>
                  {preliminary ? (
                    <span
                      className="pill p-mute"
                      title="Preliminary — awaiting the lead auditor's determination"
                    >
                      Preliminary
                    </span>
                  ) : null}
                </div>
                <p className="tpopstate">{finding.statement}</p>
                {finding.basis ? <p className="lahint">{finding.basis}</p> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const slug = (key: string) => key.replace(/_/g, "-");

/**
 * The rail is the issued plan's own agenda — one derivation, so it cannot disagree.
 *
 * **The section holding the cursor expands to its checkpoints** — M15-7. It has always said
 * which *section* was being audited and never which clause, so a reader had to reconcile the
 * agenda against the transcript by hand to find out where the audit actually was. Sections
 * not holding the cursor stay collapsed: a rail listing all 41 checkpoints is a scroll.
 */
function Rail({ rows }: { rows: RailRow[] }) {
  return (
    <div className="cleft">
      <div className="sec3">Audit agenda</div>
      {rows.map((row) => (
        <div className={`agrow ${row.state}`} key={row.key}>
          <span className="dot" />
          <span>
            <b>{row.title}</b>
            <small>
              {row.sub_range ? `Clauses ${row.sub_range}` : "Procedural"}
              {row.checkpoints > 0
                ? ` · ${row.assessed} of ${row.checkpoints} assessed`
                : ""}
            </small>
            {row.remote_blocked ? (
              <small className="blocked">
                IAF MD 5 Cl. 4.5 — not signed off remotely
              </small>
            ) : null}
            {(row.items ?? []).length > 0 ? (
              <span className="agsub">
                {(row.items ?? []).map((item) => (
                  <span className={`agcp ${item.state}`} key={item.reference}>
                    <span className="mono">{item.reference}</span>
                    <span className="agcpt">{item.title}</span>
                    {item.state === "now" ? (
                      <span className="agnow">asking now</span>
                    ) : item.outcome ? (
                      <span
                        className={`badge badge--${slug(item.outcome)}`}
                        title={
                          item.preliminary
                            ? "Preliminary — awaiting the lead auditor's determination"
                            : undefined
                        }
                      >
                        {FINDING_OUTCOMES.find((entry) => entry.key === item.outcome)
                          ?.short ?? item.outcome}
                        {item.preliminary ? "·" : ""}
                      </span>
                    ) : (
                      <span className="agnone">—</span>
                    )}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The audit clock — hours and minutes, moving.
 *
 * ⚠ **The server is the authority and this only renders between its frames.** `elapsed_hours`
 * is derived per read from `started_at` and the pauses (dictionary §10) — nothing ticks a
 * column — so a projection arrives with the true figure and the seconds between projections
 * are counted here. A pause stops it because the server stops it: `status` leaves `live` and
 * the stored total is what remains.
 *
 * It never runs backwards. The server quantises to a hundredth of an hour, so a fresh frame
 * can land up to eighteen seconds behind what the screen already showed, and a clock that
 * stutters back a minute mid-audit reads as a fault in the record. A genuine reset — a
 * session reopened, a correction — moves it by more than a minute and is taken.
 */
function AuditClock({ state }: { state: SessionState }) {
  const [seconds, setSeconds] = useState(0);
  const shown = useRef(0);

  useEffect(() => {
    const base = Math.max(0, Math.round(Number(state.elapsed_hours ?? 0) * 3600));
    const live = state.status === "live";
    const from = Date.now();
    const tick = () => {
      const value = base + (live ? Math.floor((Date.now() - from) / 1000) : 0);
      shown.current = value + 60 < shown.current ? value : Math.max(value, shown.current);
      setSeconds(shown.current);
    };
    tick();
    if (!live) return;
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [state.elapsed_hours, state.status]);

  const total = Math.max(0, Number(state.session_hours ?? 0));
  const planned = Math.round(total * 3600);
  const paused = state.status === "paused";
  // A hairline at 0:00 rather than nothing at all — an empty track reads as a bar that has
  // not loaded, and the audit day opens on exactly that figure.
  const filled = planned > 0 ? Math.min(100, Math.max(1.5, (seconds / planned) * 100)) : 0;

  return (
    <span
      className={paused ? "cclock stopped" : "cclock"}
      title={`${hm(seconds)} elapsed of the ${total} hours planned${
        paused ? " — the clock is stopped" : ""
      }`}
    >
      <TimerIcon />
      <b className="now">{hm(seconds)}</b>
      <span className="of">/ {hm(planned)}</span>
      {/* The bar does the division the figure asks the reader to do. It is decoration over
          a figure that is already stated, so it is hidden from a screen reader rather than
          given a role and a duplicate label. */}
      <span className="bar" aria-hidden="true">
        <i style={{ width: `${filled}%` }} />
      </span>
      {paused ? <span className="stopped">clock stopped</span> : null}
    </span>
  );
}

/** Seconds as `H:MM`. Hours are not padded — an audit is single-digit hours. */
function hm(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

function Thread({
  transcript,
  findings,
  typing,
  onEditFinding,
  onOpenDocument,
  resolveDocumentUrl,
  readout,
  syncingDocumentIds,
  pendingMessage,
  pendingAuthorName,
}: {
  transcript: TranscriptMessage[];
  findings: FindingRow[];
  typing?: boolean;
  onEditFinding?: (finding: FindingRow) => void;
  onOpenDocument: (documentId: string, filename: string) => void;
  resolveDocumentUrl: (documentId: string) => Promise<string>;
  readout: Readout;
  syncingDocumentIds: Set<string>;
  pendingMessage?: string | null;
  pendingAuthorName?: string | null;
}) {
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [transcript.length, typing, pendingMessage]);

  return (
    <div className="thread">
      {transcript.map((message) => (
        <Message
          key={message.id}
          message={message}
          findings={findings}
          onEditFinding={onEditFinding}
          onOpenDocument={onOpenDocument}
          resolveDocumentUrl={resolveDocumentUrl}
          readout={readout}
          syncingDocumentIds={syncingDocumentIds}
        />
      ))}
      {/* **Read where it was typed, not where it lands.** The text is held out of
          `sendAuditeeMessage` until the document it was sent with can be retrieved (migration
          0035) — but the client already watched it leave the composer, and a reply box that
          swallowed it until the sync finished would read as lost input. This is a local echo
          only: nothing here is the transcript, and it disappears the moment the real message
          arrives over the socket and takes its place. */}
      {pendingMessage ? (
        <div className="msg auditee">
          <div className="who">Client response — {pendingAuthorName ?? "the client"}</div>
          <div className="bub">{pendingMessage}</div>
        </div>
      ) : null}
      {/* **The wait, shown where the reply will appear.** Complao takes twenty to thirty
          seconds to retrieve and write (ADR 0022), and a sentence in the composer explaining
          that made the client read an apology every turn. The dots say the same thing in the
          place they are already looking and stop by themselves when the message lands. */}
      {typing ? (
        <div className="msg ai typing">
          <div className="av" aria-hidden="true">
            c
          </div>
          <div className="bub" role="status" aria-label="Complao is composing a reply">
            <i aria-hidden="true" />
            <i aria-hidden="true" />
            <i aria-hidden="true" />
          </div>
        </div>
      ) : null}
      <div ref={bottom} />
    </div>
  );
}

function Message({
  message,
  findings,
  onEditFinding,
  onOpenDocument,
  resolveDocumentUrl,
  readout,
  syncingDocumentIds,
}: {
  message: TranscriptMessage;
  findings: FindingRow[];
  onEditFinding?: (finding: FindingRow) => void;
  onOpenDocument: (documentId: string, filename: string) => void;
  resolveDocumentUrl: (documentId: string) => Promise<string>;
  readout: Readout;
  syncingDocumentIds: Set<string>;
}) {
  if (message.author === "system") {
    return (
      <div className="msg system">
        <div className="bub">
          {message.body} · {clock(message.created_at)}
        </div>
      </div>
    );
  }

  // A recorded finding is a card rather than a bubble, because the report reads its fields
  // and the transcript should show what the report will hold.
  if (message.kind === "finding") {
    const finding = findings.find(
      (row) => row.id === (message.payload?.finding_id as string | undefined),
    );
    // ⚠ **Preliminary is a different thing from recorded, and must never read as the same.**
    // ADR 0024 shows the client a model's proposal the moment it is made; Cl. 9.4.5 puts the
    // determination of findings with the audit team. Both facts have to be on the card at
    // once or the first silently becomes the second. The flag comes from the payload rather
    // than being inferred from a null disposition — the payload is what was true when the
    // client read it, and a later disposition must not rewrite the label on that message.
    const preliminary =
      Boolean(message.payload?.preliminary) && finding?.disposition == null;
    const proposed = message.payload?.origin === "ai_proposed";
    return (
      <div className="msg lead_auditor" style={{ maxWidth: "100%" }}>
        <div className="who">
          {proposed
            ? preliminary
              ? "Complao — preliminary classification"
              : "Complao — proposal, decided by the lead auditor"
            : "Lead auditor — finding recorded"}
          {message.clause_ref ? <span className="cl">Cl. {message.clause_ref}</span> : null}
        </div>
        <FindingCard
          message={message}
          finding={finding}
          preliminary={preliminary}
          onEdit={finding && onEditFinding ? () => onEditFinding(finding) : undefined}
        />
      </div>
    );
  }

  // A document the client sent, shown where the turn it belongs to is read — M14a-1. The
  // card is built from the payload record_upload/append_message wrote, never from prose: a
  // model does not narrate what was attached.
  if (message.kind === "document") {
    return (
      <div className="msg auditee">
        <div className="who">
          {message.clause_ref ? <span className="cl">Cl. {message.clause_ref}</span> : null}
          {`Client sent a document — ${message.author_name ?? "the client"}`}
        </div>
        <DocumentCard
          message={message}
          onOpen={onOpenDocument}
          resolveUrl={resolveDocumentUrl}
          syncing={syncingDocumentIds.has(String(message.payload?.document_id ?? ""))}
        />
      </div>
    );
  }

  // The lead auditor's decision on a proposal the client has already read — ADR 0024.
  // A message, never an edit of the one they read: silently rewriting a record the client
  // demonstrably saw is the thing an accreditation assessor objects to.
  if (message.kind === "disposition") {
    const disposition = String(message.payload?.disposition ?? "");
    return (
      <div className="msg lead_auditor" style={{ maxWidth: "100%" }}>
        <div className="who">
          Lead auditor — determination
          {message.clause_ref ? <span className="cl">Cl. {message.clause_ref}</span> : null}
        </div>
        <div className={`dispcard disp--${disposition}`}>
          {message.body}
          <span className="ts">{clock(message.created_at)}</span>
        </div>
      </div>
    );
  }

  if (message.kind === "section_summary") {
    return <SectionSummary message={message} />;
  }

  if (message.author === "ai") {
    return (
      <div className="msg ai">
        <div className="av">c</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="who">
            Complao audit team member
            {message.clause_ref ? (
              <span className="cl">Cl. {message.clause_ref}</span>
            ) : message.payload?.item ? (
              <span className="cl">{String(message.payload.item)}</span>
            ) : null}
            {/* **Read aloud — M31, and only here.** This is the one bubble that is Complao
                speaking to the room, so it is the one thing offered to the speaker. What is
                deliberately *not* offered: a client's answer or a lead auditor's note —
                testimony re-voiced by a machine reads differently from testimony, and the
                question of whether a synthesiser stressed the right word in someone's
                evidence is not one an audit record should be able to raise. Nor a finding
                card, a disposition or a section summary: those are the grades, they are
                read from a table and a badge, and a voice reading one out would be the
                platform stating a determination in prose. */}
            <ReadoutButton readout={readout} id={message.id} body={message.body ?? ""} />
          </div>
          {/* **Only Complao's own words are formatted.** The model writes for a reader who
              is not an auditor, so the prompt asks it for a document name in bold and
              sub-parts as a list — and until this rendered, the client read the asterisks.
              The client's and the lead auditor's bubbles below stay verbatim: those are
              somebody's testimony, and re-styling what a person typed changes how the
              record reads to whoever reviews it afterwards. */}
          <div className="bub bub--rich">
            <RichText text={message.body ?? ""} />
            <span className="ts">{clock(message.created_at)}</span>
          </div>
          {/* Why nothing was heard, under the message it was pressed on rather than in the
              console's banner. A speaker that fails silently is indistinguishable from one
              that is working on a device with no voice installed, and the listener has
              nothing on screen to tell them otherwise. */}
          {readout.problem?.id === message.id ? (
            <p className="hint readout-problem" role="alert">
              {readout.problem.message}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  const inadequate = Boolean(message.flags?.inadequate);
  return (
    <div className={`msg ${message.author}`}>
      <div className="who">
        {inadequate ? (
          <span className="flagpill" title="Complao judged this insufficient to record">
            ⚠ Insufficient
          </span>
        ) : null}
        {message.clause_ref ? <span className="cl">Cl. {message.clause_ref}</span> : null}
        {message.author === "auditee"
          ? `Client response — ${message.author_name ?? "the client"}`
          : `Lead auditor ${message.payload?.intervention ?? "note"}`}
      </div>
      <div className="bub">
        {message.body}
        <span className="ts">{clock(message.created_at)}</span>
      </div>
    </div>
  );
}

/**
 * The speaker on one of Complao's replies — M31.
 *
 * Pressed, never automatic, and it plays what the bubble above it says: `spokenText` is the
 * same subset `RichText` renders, so the asterisks the client stopped reading are not now
 * read to them.
 *
 * It is drawn only where it can work. Where the API is absent there is no disabled speaker
 * left standing on every message in the transcript — that differs from M30's mic, which was
 * a control the prototype had already drawn and promised, appearing once. A device that has
 * the API but no voice cannot be detected until something is played, so that failure is
 * reported under the message instead.
 */
function ReadoutButton({
  readout,
  id,
  body,
}: {
  readout: Readout;
  id: string;
  body: string;
}) {
  const words = spokenText(body);
  if (!readout.supported || !words) return null;
  const speaking = readout.speaking === id;

  return (
    <button
      type="button"
      className={speaking ? "readout on" : "readout"}
      title={speaking ? "Stop reading" : "Read this message aloud"}
      aria-label={speaking ? "Stop reading this message" : "Read this message aloud"}
      aria-pressed={speaking}
      onClick={() => readout.toggle(id, words)}
    >
      <SpeakerIcon />
      <span>{speaking ? "Stop" : "Listen"}</span>
    </button>
  );
}

function SpeakerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

function FindingCard({
  message,
  finding,
  preliminary,
  onEdit,
}: {
  message: TranscriptMessage;
  finding?: FindingRow;
  /** A model proposed this and no auditor has decided it yet — ADR 0024. */
  preliminary?: boolean;
  onEdit?: () => void;
}) {
  const payload = message.payload ?? {};
  const outcome = String(finding?.outcome ?? payload.outcome ?? "");
  const grade = FINDING_OUTCOMES.find((entry) => entry.key === outcome);
  // **The basis is absent rather than hidden.** The client's projection returns it null on
  // both the findings row and the payload (`_basis_withheld`), so this row is drawn from
  // whether there is a sentence to draw — one reading, not a second copy of the rule about
  // who may read it. Everyone else's projection carries it and the row appears for them.
  const basis = finding?.basis ?? (payload.basis == null ? null : String(payload.basis));

  return (
    <div className={preliminary ? "findcard prelim" : "findcard"}>
      <div className="fhead">
        <b>{preliminary ? "Preliminary classification" : "Finding recorded"}</b>
        {onEdit ? (
          <button type="button" className="btn sec sm" onClick={onEdit}>
            Edit
          </button>
        ) : null}
      </div>
      <dl>
        <dt>Clause</dt>
        <dd>Cl. {finding?.clause_ref ?? String(payload.clause_ref ?? "—")}</dd>
        <dt>Section</dt>
        <dd>Clause {finding?.section ?? "—"}</dd>
        <dt>Classification</dt>
        <dd>
          <span className={`badge badge--${slug(outcome)}`}>{grade?.label ?? outcome}</span>
        </dd>
        <dt>Evidence presented</dt>
        <dd>{finding?.statement ?? String(payload.statement ?? "")}</dd>
        {basis ? (
          <>
            <dt>Basis for classification</dt>
            <dd>{basis}</dd>
          </>
        ) : null}
        <dt>Recorded by</dt>
        <dd>
          {recordedBy(finding, payload, message.author_name)} · {clock(message.created_at)}
        </dd>
      </dl>
      {preliminary ? (
        <p className="fprelim">
          Subject to the lead auditor's determination. Audit findings are determined by the
          audit team — ISO/IEC 17021-1 Cl. 9.4.5.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The document register — everything the client sent, and what the auditor has been through.
 *
 * ⚠ **It used to be the readiness checklist.** Rows were expected-evidence slots and an
 * upload filled one. ADR 0025 unbinds evidence from clauses: there are no slots, so the
 * register is the flat list of what arrived, newest first.
 *
 * Requests Complao raised during the session sit at the top, because assertion 13's document
 * request is the thing the client is being asked for *now* and it must not be buried under
 * the eighteen documents they already sent.
 */
function Register({
  state,
  onUpload,
  onMark,
  onMarkAll,
  onOpenDocument,
}: {
  state: SessionState;
  onUpload?: (files: File[], answering?: string) => Promise<void>;
  /** The lead auditor's console only — M15-6. Absent everywhere else, including the client's. */
  onMark?: (documentId: string, reviewed: boolean) => Promise<void>;
  /** The lead auditor's console only. The same mark, over every row at once. */
  onMarkAll?: (reviewed: boolean) => Promise<void>;
  onOpenDocument: (documentId: string, filename: string) => void;
}) {
  const documents = state.documents ?? [];
  const requests = state.requests ?? [];
  const reviewed = documents.filter((row) => row.status === "reviewed").length;
  /** Both controls take a moment — one builds a zip, the other writes a row per document. */
  const [working, setWorking] = useState<"download" | "mark" | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /**
   * The archive goes through the certification body's own credential, so the client's
   * console does not offer it — their copies are the files they sent, and the portal has no
   * archive route. An observing certification body reads the evidence of its own audit.
   */
  const canDownload =
    documents.length > 0 &&
    (state.viewer.role === "lead_auditor" || state.viewer.role === "cb");
  /** Everything already marked, so the press puts the marks back rather than setting them. */
  const allReviewed = documents.length > 0 && reviewed === documents.length;

  const downloadAll = async () => {
    setWorking("download");
    setFailed(null);
    try {
      const archive = await fetchAuditDocumentArchive(state.audit_id);
      const anchor = window.document.createElement("a");
      anchor.href = archive.url;
      anchor.download = archive.filename;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(archive.url), 0);
    } catch {
      // Said out loud. A download that fails silently mid-audit leaves an auditor waiting
      // for a file that is never coming.
      setFailed("The archive could not be built. Try again, or open the documents singly.");
    } finally {
      setWorking(null);
    }
  };

  const markAll = async () => {
    if (!onMarkAll) return;
    setWorking("mark");
    setFailed(null);
    try {
      await onMarkAll(!allReviewed);
    } catch {
      setFailed("The register could not be marked.");
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="cdocs">
      <div className="sec3" style={{ padding: "14px 14px 8px" }}>
        Document register
      </div>
      <div className="dcount">
        <div>
          <b>{documents.length}</b>
          <small>Received</small>
        </div>
        <div>
          <b>{reviewed}</b>
          <small>Reviewed</small>
        </div>
        <div>
          <b>{requests.filter((row) => row.status !== "received").length}</b>
          <small>Requested</small>
        </div>
      </div>

      {/* The two things done to the register as a whole, under the counts they act on:
          take it away, and say it has been read. Both are absent rather than disabled where
          the viewer may not use them — the mark is the audit team's statement and the
          archive is the certification body's read of its own client's evidence. */}
      {canDownload || (onMarkAll && documents.length > 0) ? (
        <div className="dbulk">
          {canDownload ? (
            <button
              type="button"
              className="btn sec sm"
              disabled={working !== null}
              onClick={() => void downloadAll()}
            >
              {working === "download" ? "Preparing\u2026" : "Download All"}
            </button>
          ) : null}
          {onMarkAll && documents.length > 0 ? (
            <button
              type="button"
              className="btn sec sm"
              disabled={working !== null}
              onClick={() => void markAll()}
              title={
                allReviewed
                  ? "Every document is marked. This puts every mark back."
                  : "Marks every document in the register as reviewed by you."
              }
            >
              {working === "mark"
                ? "Marking\u2026"
                : allReviewed
                  ? "Unmark all"
                  : "Mark Reviewed"}
            </button>
          ) : null}
        </div>
      ) : null}
      {failed ? (
        <div className="dwarn" style={{ margin: "0 14px 8px" }} role="alert">
          {failed}
        </div>
      ) : null}

      {requests.length > 0 ? (
        <>
          <div className="sec3" style={{ padding: "12px 14px 6px" }}>
            Raised in session
          </div>
          {requests.map((request) => (
            <div className="dcard" key={request.id}>
              <div className="dn">
                <b>Cl. {request.clause_ref ?? "—"}</b>
                <span className="pill p-wait" style={{ fontSize: "9px" }}>
                  {request.status === "received" ? "Received" : "Outstanding"}
                </span>
              </div>
              <div className="sub" style={{ fontSize: "11px", marginTop: "4px" }}>
                {request.description}
              </div>
              {/* **The request and the way to answer it are one control.** A request the
                  client can act on where they read it is one control; a request that sends
                  them looking for another screen is two. */}
              {onUpload && request.status !== "received" ? (
                <FileButton
                  label="Send this document"
                  onFiles={(files) => onUpload(files, request.clause_ref ?? undefined)}
                />
              ) : null}
            </div>
          ))}
        </>
      ) : null}

      {documents.map((document) => (
        <div className="dcard" key={document.id}>
          <div className="dn">
            {/* The filename opens the file — M15-6. Evidence that cannot be opened is
                evidence taken on trust, and a lead auditor signs findings that follow
                from it. */}
            <button
              type="button"
              className="linky"
              onClick={() => onOpenDocument(document.id, document.filename)}
            >
              {document.filename}
            </button>
            {onMark ? (
              // ⚠ **The lead auditor's mark, and nothing runs behind it** — no evaluation,
              // no validation, no retrieval. The client sees the state and cannot set it:
              // an assessor reads *reviewed* as a statement by the audit team.
              <button
                type="button"
                className={
                  document.status === "reviewed"
                    ? "pill p-ok dmark"
                    : "pill p-wait dmark"
                }
                style={{ fontSize: "9px" }}
                onClick={() =>
                  void onMark(document.id, document.status !== "reviewed")
                }
              >
                {document.status === "reviewed" ? "Reviewed" : "Not reviewed"}
              </button>
            ) : (
              <span
                className={
                  document.status === "reviewed" ? "pill p-ok" : "pill p-wait"
                }
                style={{ fontSize: "9px" }}
              >
                {document.status === "reviewed" ? "Reviewed" : "Not reviewed"}
              </span>
            )}
          </div>
          <div className="dmeta">
            {document.uploaded_by} · {clock(document.uploaded_at)}
          </div>
          {/* ⚠ A knowledge base does not OCR. A scan ingests as zero retrievable text,
              successfully and silently, and the clause it was evidence for then reads
              unevidenced with nothing saying why. */}
          {document.extraction_ok === false ? (
            <div className="dwarn">⚠ No readable text — a scan or an image</div>
          ) : null}
        </div>
      ))}

      <div className="dfoot">
        All documents form part of the audit record per IAF MD 4:2025.
      </div>
    </div>
  );
}

/**
 * A document's bytes, as a blob URL the caller revokes — the auditee reaches their own
 * upload through the portal's own credential, everyone else through the certification
 * body's. One branch, so a card and the register's filename link cannot disagree about it.
 */
async function documentUrl(state: SessionState, documentId: string): Promise<string> {
  return state.viewer.role === "auditee"
    ? fetchPortalDocument(documentId)
    : fetchAuditDocument(state.audit_id, documentId);
}

/**
 * Open one document in a new tab.
 *
 * Fetched rather than linked: the route is authenticated and an `<a href>` carries no bearer
 * token in the URL itself.
 */
async function openDocument(
  state: SessionState,
  documentId: string,
  filename: string,
): Promise<void> {
  const url = await documentUrl(state, documentId);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener";
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * One upload, with its own in-flight and failure state — M14a-1.
 *
 * A real `<input type="file">` behind a label rather than a button that clicks a hidden
 * input: the native control is the one a screen reader and a keyboard already understand,
 * and the file dialog will not open from script in every browser anyway.
 */
function FileButton({
  label,
  onFiles,
}: {
  label: string;
  onFiles: (files: File[]) => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  return (
    <label className={sending ? "dsend busy" : "dsend"}>
      <input
        type="file"
        multiple
        disabled={sending}
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? []);
          // Cleared either way, so choosing the same file twice after a failure still fires.
          event.target.value = "";
          if (chosen.length === 0) return;
          setSending(true);
          setFailed(null);
          void onFiles(chosen)
            .catch((cause: unknown) =>
              setFailed(cause instanceof Error ? cause.message : "That did not send."),
            )
            .finally(() => setSending(false));
        }}
      />
      <span>{sending ? "Sending…" : label}</span>
      {failed ? <em className="dfail">{failed}</em> : null}
    </label>
  );
}

/**
 * A small preview of a document sent in the transcript, opening the same way as the
 * register's own filename link. An image gets a real thumbnail — lazy-fetched once, on
 * mount, and revoked when the card unmounts; anything else gets a tile named for its
 * extension, since there is no thumbnail to generate for a spreadsheet.
 */
function DocumentCard({
  message,
  onOpen,
  resolveUrl,
  syncing,
}: {
  message: TranscriptMessage;
  onOpen: (documentId: string, filename: string) => void;
  resolveUrl: (documentId: string) => Promise<string>;
  //: Still being indexed into the audit's knowledge base — the composer is locked for the
  //: same reason (`corpus_sync`), and this is the one tile it belongs to.
  syncing: boolean;
}) {
  const payload = message.payload ?? {};
  const documentId = String(payload.document_id ?? "");
  const filename = String(payload.filename ?? "document");
  const isImage = String(payload.mime_type ?? "").startsWith("image/");
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage || !documentId) return undefined;
    let objectUrl: string | null = null;
    let cancelled = false;
    void resolveUrl(documentId).then((url) => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setThumbnail(url);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // `resolveUrl` is a fresh closure every render (it closes over `state`, which changes on
    // every socket frame) — depending on it would refetch and re-mint a blob URL on every
    // frame for the life of the session. `documentId` never changes for a given message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, isImage]);

  return (
    <div>
      <div className="doccard-row">
        {syncing ? (
          <span
            className="doccard-syncing"
            role="status"
            aria-label="Indexing this document"
            title="Indexing this document — this finishes automatically"
          />
        ) : null}
        <button type="button" className="doccard" onClick={() => onOpen(documentId, filename)}>
          <span className="doccard-thumb">
            {thumbnail ? (
              <img src={thumbnail} alt="" />
            ) : (
              <span className="doccard-ext">{extensionLabel(filename)}</span>
            )}
          </span>
          <span className="doccard-name">{filename}</span>
        </button>
      </div>
      {payload.extraction_ok === false ? (
        <div className="dwarn">⚠ No readable text — a scan or an image</div>
      ) : null}
      {payload.duplicate === true ? (
        <div className="dwarn">⚠ Duplicate file — not uploaded again</div>
      ) : null}
    </div>
  );
}

export function extensionLabel(filename: string): string {
  const match = /\.([a-zA-Z0-9]+)$/.exec(filename);
  return match?.[1] ? match[1].toUpperCase().slice(0, 4) : "FILE";
}

/**
 * What a section found, at its boundary — M15-5, the client's item 7.
 *
 * ⚠ **Rendered from the payload, which is derived from `findings` on the server.** Nothing
 * here is prose a model wrote: a paraphrased grade in front of the client is a second
 * statement of the same finding that can differ from the first, and an empty summary posted
 * as though full is exactly what the certification body's own logs are full of.
 */
function SectionSummary({ message }: { message: TranscriptMessage }) {
  const payload = message.payload ?? {};
  const rows = (payload.rows ?? []) as {
    clause_ref: string;
    title: string;
    outcome: string | null;
    preliminary: boolean;
    withdrawn?: boolean;
  }[];

  return (
    <div className="msg ai" style={{ maxWidth: "100%" }}>
      <div className="av">c</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="who">Section {String(payload.section ?? "")} — summary of findings</div>
        <div className="sumcard">
          <table className="sumtable">
            <thead>
              <tr>
                <th>Clause</th>
                <th>Requirement</th>
                <th>Classification</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const grade = FINDING_OUTCOMES.find((entry) => entry.key === row.outcome);
                return (
                  <tr key={row.clause_ref}>
                    <td className="mono">{row.clause_ref}</td>
                    <td>{row.title}</td>
                    <td>
                      {row.outcome === null || row.withdrawn ? (
                        <span className="sub">
                          {row.withdrawn ? "Withdrawn — no finding" : "No finding recorded"}
                        </span>
                      ) : (
                        <>
                          <span className={`badge badge--${slug(row.outcome)}`}>
                            {grade?.label ?? row.outcome}
                          </span>
                          {row.preliminary ? (
                            <span className="prelimtag"> preliminary</span>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <span className="ts">{clock(message.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function clock(value: string): string {
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
